import { useCallback, useEffect, useRef, useState } from 'react';

import { FinancialConfig } from '@futrli-sdk/predict';

import { convertExistingDriver, generateFinancialYearData, generateNewDriver, getAmountsFromRows } from './utils';
import { FreestyleMethodRowData } from '../../../ui/data-builder/freestyle-content-form/types';
import { FreestyleNonFinancialDriver } from '../../../core/api/types/non-financial-drivers';

const DRIVER_INSTANCE_ID = 0; // This is a legacy thing, we dont use this anymore. Needed for now to use existing utils code.

export const useFreestyleDriverForm = (
  driver?: FreestyleNonFinancialDriver,
  financialConfig?: FinancialConfig,
  resourceKey = ''
) => {
  const [rows, setRows] = useState<FreestyleMethodRowData[]>([]);
  const [storeOnThe, setStoreOnThe] = useState('32');
  const [yearsAdded, setYearsAdded] = useState<string[]>([]);
  const initialized = useRef<{
    driver: FreestyleNonFinancialDriver | undefined;
    datesKey: string;
    resourceKey: string;
  }>();

  const getRequestData = useCallback(() => {
    return {
      amounts: getAmountsFromRows(rows),
      output_occurs_on_day: parseInt(storeOnThe, 10)
    };
  }, [rows, storeOnThe]);

  // ************
  // * Handlers *
  // ************

  const handleAddFinancialYear = useCallback(() => {
    if (financialConfig) {
      const lastFinancialYear = parseInt(rows[rows.length - 1].date, 10);
      const newYearData = generateFinancialYearData(DRIVER_INSTANCE_ID, financialConfig, lastFinancialYear + 1);

      setYearsAdded([...yearsAdded, newYearData.date]);
      setRows([...rows, newYearData]);
    }
  }, [financialConfig, rows, yearsAdded]);

  const handleChange = useCallback((newRows: FreestyleMethodRowData[]) => {
    setRows(newRows);
  }, []);

  const handleStoreOnTheChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setStoreOnThe(event.target.value);
  }, []);

  // ***********
  // * Effects *
  // ***********

  // Create row data for us to display - convert given driver or generate new rows (if no driver specified)
  useEffect(() => {
    if (financialConfig) {
      const datesKey = JSON.stringify([financialConfig.current_year, financialConfig.year_end]);
      if (
        initialized.current?.driver === driver &&
        initialized.current?.datesKey === datesKey &&
        initialized.current?.resourceKey === resourceKey
      ) {
        return;
      }
      initialized.current = { driver, datesKey, resourceKey };
      if (driver) {
        setRows(convertExistingDriver(DRIVER_INSTANCE_ID, financialConfig, driver));
        setStoreOnThe(driver.output_occurs_on_day.toString());
        setYearsAdded([]);
      } else {
        setRows(generateNewDriver(DRIVER_INSTANCE_ID, financialConfig));
        setStoreOnThe('32');
        setYearsAdded([]);
      }
    }
  }, [driver, financialConfig, resourceKey]);

  return {
    getRequestData,
    handleAddFinancialYear,
    handleChange,
    handleStoreOnTheChange,
    rows,
    storeOnThe,
    yearsAdded
  };
};
